import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtINR, fmtNum, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { ChevronLeft, ChevronRight, Fuel, Camera, Upload, CheckCircle2, Save, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
// MeterScanner removed from wizard — kept in Upload Inbox for post-hoc
// comparison. See UploadInbox.jsx + meter_ocr.py.

/**
 * Filler Wizard — ultra-simplified mobile-first shift entry.
 *
 * Steps:
 *   1. Pick shift + your name
 *   2. Pick nozzles you fueled + scan/type opening & closing per nozzle
 *   3. Upload proof photos (bulk drop)
 *   4. Review & submit — kicked to next reviewer (cashier/supervisor)
 *
 * A filler ONLY enters readings. Payments, cash, debtors are locked and hidden;
 * the cashier picks up the same entry later to complete it.
 */
export default function FillerWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const [nozzles, setNozzles] = useState([]);
  const [prices, setPrices] = useState({ HSD: 0, MS: 0, CNG: 0 });
  const [shiftMode, setShiftMode] = useState("3-shift");

  const [date, setDate] = useState(todayISO());
  const [shiftCode, setShiftCode] = useState("SC-01");
  const [attendee, setAttendee] = useState(user?.name || "");

  // Selected nozzle set + per-nozzle reading rows
  const [selected, setSelected] = useState(new Set());
  const [readings, setReadings] = useState({});  // nozzle_id → { opening, closing, trial_litres }

  const [proofFiles, setProofFiles] = useState([]);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [nz, cfg] = await Promise.all([
          api.get("/masters/nozzles"),
          api.get("/masters/config"),
        ]);
        setNozzles((nz.data || []).filter((n) => n.active !== false));
        setShiftMode(cfg.data?.SHIFT_MODE || "3-shift");
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get(`/masters/fuel_prices/for/${date}`);
        setPrices({ HSD: p.data.hsd, MS: p.data.ms, CNG: p.data.cng });
      } catch (err) {
        // No price row for this date yet — owner will set it before shift.
        console.debug("filler wizard: fuel_prices lookup failed", err?.response?.status);
      }
    })();
  }, [date]);

  const shifts = shiftMode === "2-shift" ? ["SC-D", "SC-N"] : ["SC-01", "SC-02", "SC-03"];

  const grouped = useMemo(() => ({
    HSD: nozzles.filter((n) => n.fuel === "HSD"),
    MS: nozzles.filter((n) => n.fuel === "MS"),
    CNG: nozzles.filter((n) => n.fuel === "CNG"),
  }), [nozzles]);

  const toggleNozzle = (nz) => {
    const s = new Set(selected);
    if (s.has(nz.nozzle_id)) { s.delete(nz.nozzle_id); }
    else {
      s.add(nz.nozzle_id);
      // Pre-fill opening from last closing (backend derives on submit but we hint here)
      fetchPrevClose(nz.nozzle_id);
    }
    setSelected(s);
  };

  const fetchPrevClose = async (nozzle_id) => {
    try {
      const r = await api.get(`/shifts/nozzle/${nozzle_id}/last_closing`, { params: { before_date: date, before_shift: shiftCode } }).catch(() => null);
      if (r?.data?.last_closing != null) {
        setReadings((prev) => ({ ...prev, [nozzle_id]: { ...(prev[nozzle_id] || {}), opening: r.data.last_closing } }));
      }
    } catch (err) {
      console.debug("filler wizard: last_closing lookup failed", nozzle_id, err?.response?.status);
    }
  };

  const updReading = (nz_id, patch) => setReadings((prev) => ({ ...prev, [nz_id]: { ...(prev[nz_id] || {}), ...patch } }));

  // Live per-nozzle preview
  const rowStats = (nz) => {
    const r = readings[nz.nozzle_id] || {};
    const o = Number(r.opening) || 0;
    const c = Number(r.closing) || 0;
    const t = Number(r.trial_litres) || 0;
    const gross = Math.max(0, c - o);
    const net = Math.max(0, gross - t);
    const price = prices[nz.fuel] || 0;
    return { gross, net, price, amount: net * price, invalid: c > 0 && c <= o };
  };

  const totalPreview = useMemo(() => {
    // NEVER mash litres and kg. CNG is sold by weight (kg); MS/HSD by volume (L).
    // Only rupees are summable across all three fuels.
    let litres = 0, kg = 0, cash = 0;
    for (const nz of nozzles) {
      if (!selected.has(nz.nozzle_id)) continue;
      const s = rowStats(nz);
      if (s.invalid) continue;
      if (nz.fuel === "CNG") kg += s.net; else litres += s.net;
      cash += s.amount;
    }
    return { litres, kg, cash };
  }, [readings, selected, nozzles, prices]); // eslint-disable-line

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("date", date);
    fd.append("shift_code", shiftCode);
    fd.append("note", "Filler wizard");
    const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const onProofPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const done = [];
    for (const f of files) {
      try {
        const u = await uploadOne(f);
        done.push(u);
      } catch (err) { toast.error(`Upload failed: ${f.name}`); }
    }
    setUploaded((prev) => [...prev, ...done]);
    setProofFiles((prev) => [...prev, ...files.map((f) => f.name)]);
    setBusy(false);
    if (done.length) toast.success(`Uploaded ${done.length} photo${done.length > 1 ? "s" : ""}`);
  };

  const canSubmit = () => {
    if (!attendee.trim()) return false;
    if (selected.size === 0) return false;
    // every selected nozzle must have opening & closing and be valid
    for (const nz of nozzles) {
      if (!selected.has(nz.nozzle_id)) continue;
      const r = readings[nz.nozzle_id] || {};
      if (r.opening == null || r.closing == null) return false;
      if (Number(r.closing) <= Number(r.opening)) return false;
    }
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) { toast.error("Some readings are incomplete or invalid"); return; }
    setBusy(true);
    try {
      const payload = {
        date, shift_code: shiftCode,
        attendee_names: [attendee],
        cashier_name: "",  // filler doesn't set cashier — cashier claims later
        readings: [...selected].map((nz_id) => {
          const nz = nozzles.find((x) => x.nozzle_id === nz_id);
          const r = readings[nz_id] || {};
          return {
            nozzle_id: nz_id,
            opening: Number(r.opening) || 0,
            closing: Number(r.closing) || 0,
            trial_litres: Number(r.trial_litres) || 0,
            price: prices[nz.fuel] || 0,
          };
        }),
        adjustments: [],
        lubricants: [],
        payments: [],
        debtors: [],
        cash_in_hand_fuels: 0,
        cash_in_hand_cng: 0,
        draft: true,   // filler creates as DRAFT; cashier completes
      };
      const r = await api.post("/shifts/", payload);
      toast.success("Shift readings saved — handed to cashier");
      setTimeout(() => nav(`/shifts/${r.data.entry_id}`), 800);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-16" data-testid="filler-wizard-root">
      <Toaster position="top-center" />

      {/* Header */}
      <div className="bg-slate-900 text-white p-4 -mx-3 sm:rounded-sm sm:mx-0">
        <div className="text-xs uppercase tracking-wider text-slate-300">Filler · Step {step} of 4</div>
        <h1 className="text-2xl font-bold mt-0.5">{["Shift & attendee", "Pick nozzles & readings", "Upload proofs", "Review & submit"][step - 1]}</h1>
        {/* progress bar */}
        <div className="flex gap-1 mt-3">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={"h-1.5 flex-1 rounded-full " + (n <= step ? "bg-emerald-400" : "bg-slate-700")} />
          ))}
        </div>
      </div>

      {step === 1 && (
        <Card className="p-4 border-2 border-slate-300 rounded-sm space-y-4" data-testid="wizard-step-1">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12 border-2 border-slate-300 text-lg" data-testid="filler-date" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Shift</label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="filler-shift-picker">
              {shifts.map((s) => (
                <button key={s} type="button" onClick={() => setShiftCode(s)}
                        data-testid={`filler-shift-${s}`}
                        className={"h-14 border-2 rounded-sm font-bold " + (shiftCode === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1"><User size={12} />Your name</label>
            <Input value={attendee} onChange={(e) => setAttendee(e.target.value)} placeholder="Filler name" className="h-12 border-2 border-slate-300 text-lg" data-testid="filler-attendee" />
          </div>
          <Button data-testid="filler-next-1" onClick={() => setStep(2)} disabled={!attendee.trim()} className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-bold">
            Next: Pick nozzles <ChevronRight size={20} className="ml-1" />
          </Button>
        </Card>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="wizard-step-2">
          <Card className="p-3 border-2 border-slate-300 rounded-sm">
            <h3 className="font-bold text-slate-800 mb-2 text-sm uppercase tracking-wider">Tap the nozzles you dispensed from</h3>
            {["HSD", "MS", "CNG"].map((fuel) => (
              <div key={fuel} className="mb-3">
                <div className="text-xs font-bold text-slate-600 mb-1">{fuel} <span className="text-slate-400 font-normal">· ₹{prices[fuel] || "—"}/{fuel === "CNG" ? "kg" : "L"}</span></div>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
                  {grouped[fuel].map((nz) => {
                    const on = selected.has(nz.nozzle_id);
                    const num = nz.label.replace(/^[A-Z]+ - Nozzle\s*/i, "");
                    return (
                      <button key={nz.nozzle_id} type="button" onClick={() => toggleNozzle(nz)}
                              data-testid={`filler-nz-chip-${nz.nozzle_id}`}
                              className={"h-12 border-2 rounded-sm text-sm font-bold " + (on ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300")}>
                        {num}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </Card>

          {selected.size > 0 && (
            <Card className="p-3 border-2 border-slate-300 rounded-sm space-y-3">
              <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Enter readings ({selected.size} nozzle{selected.size > 1 ? "s" : ""})</h3>
              {nozzles.filter((nz) => selected.has(nz.nozzle_id)).map((nz) => {
                const r = readings[nz.nozzle_id] || {};
                const s = rowStats(nz);
                return (
                  <div key={nz.nozzle_id} className="border-2 border-slate-200 rounded-sm p-3" data-testid={`filler-reading-${nz.nozzle_id}`}>
                    <div className="font-bold text-slate-900 mb-2">{nz.label}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Opening</label>
                        <Input type="number" step="0.01" value={r.opening ?? ""} onChange={(e) => updReading(nz.nozzle_id, { opening: e.target.value })} className="h-12 border-2 border-slate-300 num text-lg" data-testid={`filler-open-${nz.nozzle_id}`} />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Closing</label>
                        <div className="flex items-center gap-1">
                          <Input type="number" step="0.01" value={r.closing ?? ""} onChange={(e) => updReading(nz.nozzle_id, { closing: e.target.value })} className="h-12 border-2 border-slate-300 num text-lg flex-1" data-testid={`filler-close-${nz.nozzle_id}`} />
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                      {/* Phase-3 (2026-07-21): in-row meter scan button removed.
                          OCR now runs post-hoc from Upload Inbox so it never
                          slows down daily manual entry. */}
                      <div className="text-xs text-slate-600 text-right ml-auto">
                        {s.invalid ? <span className="text-red-600 font-bold">Closing must be greater than opening</span>
                                    : (s.net > 0 ? <>Net: <span className="font-bold text-slate-900">{fmtNum(s.net, 2)}</span> {nz.fuel === "CNG" ? "kg" : "L"} · <span className="font-bold text-emerald-700">{fmtINR(s.amount)}</span></> : <span className="text-slate-400">Awaiting closing…</span>)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="bg-slate-100 border-2 border-slate-300 rounded-sm p-3 flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-slate-600 font-bold">Shift total (preview)</div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">
                    {totalPreview.litres > 0 && <span>{fmtNum(totalPreview.litres, 2)} L (MS/HSD)</span>}
                    {totalPreview.litres > 0 && totalPreview.kg > 0 && <span> · </span>}
                    {totalPreview.kg > 0 && <span>{fmtNum(totalPreview.kg, 2)} kg (CNG)</span>}
                    {totalPreview.litres === 0 && totalPreview.kg === 0 && <span>—</span>}
                  </div>
                  <div className="text-2xl num font-bold text-slate-900" data-testid="filler-total-preview">{fmtINR(totalPreview.cash)}</div>
                </div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setStep(1)} variant="outline" className="h-14 border-2 border-slate-300 text-lg"><ChevronLeft /> Back</Button>
            <Button data-testid="filler-next-2" onClick={() => setStep(3)} disabled={!canSubmit()} className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-bold">Next <ChevronRight /></Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <Card className="p-4 border-2 border-slate-300 rounded-sm space-y-3" data-testid="wizard-step-3">
          <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Upload proofs (meter photos, receipts)</h3>
          <p className="text-xs text-slate-600">Tap the button below to snap or pick photos. They&apos;ll be routed to the right slot automatically after you submit.</p>
          <label className="block">
            <input type="file" accept="image/*,application/pdf" capture="environment" multiple onChange={onProofPick} className="hidden" data-testid="proof-upload-input" />
            <div className="border-2 border-dashed border-slate-400 rounded-sm p-8 text-center bg-slate-50 hover:bg-slate-100 cursor-pointer">
              <Camera className="mx-auto text-slate-500" size={40} />
              <div className="mt-2 font-bold text-slate-800">{busy ? "Uploading…" : "Tap to upload photos"}</div>
              <div className="text-xs text-slate-500 mt-1">Meter readings · Cash slips · Card receipts · Any proof</div>
            </div>
          </label>
          {uploaded.length > 0 && (
            <div className="space-y-1" data-testid="uploaded-list">
              {uploaded.map((u) => (
                <div key={u.file_id} className="flex items-center gap-2 text-sm text-slate-800 bg-emerald-50 border border-emerald-300 rounded-sm p-2">
                  <CheckCircle2 size={14} className="text-emerald-700 shrink-0" />
                  <span className="truncate flex-1">{u.original_name || u.file_id}</span>
                  <span className="text-xs text-slate-500">{Math.round((u.size || 0) / 1024)} KB</span>
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setStep(2)} variant="outline" className="h-14 border-2 border-slate-300 text-lg"><ChevronLeft /> Back</Button>
            <Button data-testid="filler-next-3" onClick={() => setStep(4)} className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-bold">Next <ChevronRight /></Button>
          </div>
        </Card>
      )}

      {step === 4 && (
        <Card className="p-4 border-2 border-slate-900 rounded-sm space-y-4" data-testid="wizard-step-4">
          <h3 className="font-bold text-slate-900 text-lg">Review</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <ReviewLine label="Date" value={date} />
            <ReviewLine label="Shift" value={shiftCode} />
            <ReviewLine label="Attendee" value={attendee} />
            <ReviewLine label="Nozzles" value={String(selected.size)} />
            <ReviewLine label="MS/HSD (litres)" value={`${fmtNum(totalPreview.litres, 2)} L`} />
            <ReviewLine label="CNG (kilograms)" value={`${fmtNum(totalPreview.kg, 2)} kg`} />
            <ReviewLine label="Total ₹ (approx)" value={fmtINR(totalPreview.cash)} />
            <ReviewLine label="Photos uploaded" value={String(uploaded.length)} />
          </div>
          <div className="text-xs text-slate-600 bg-amber-50 border border-amber-300 p-3 rounded-sm">
            After submit, this shift goes to the <strong>cashier</strong> to add payments, debtor sales, and reconcile cash. You&apos;re done once you tap the button below.
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setStep(3)} variant="outline" className="h-14 border-2 border-slate-300 text-lg"><ChevronLeft /> Back</Button>
            <Button data-testid="filler-submit" onClick={submit} disabled={busy || !canSubmit()} className="h-14 bg-slate-900 hover:bg-slate-800 text-white text-lg font-bold">
              <Save size={20} className="mr-1" /> {busy ? "Saving…" : "Submit shift"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function ReviewLine({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-sm p-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-bold text-slate-900 mt-0.5 truncate">{value || "—"}</div>
    </div>
  );
}
